import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/finally';
import { ColDef, ColGroupDef, GridOptions, ICellRendererParams } from 'ag-grid';
import { ColumnState } from 'ag-grid/dist/lib/columnController/columnController';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { BtnCellRendererI } from 'app/core/interfaces/Btn-cell-renderer.interface';
import { BtnCellRendererComponent } from 'app/pages/A2-utils/grid-components/btn-cell-renderer/btn-cell-renderer.component';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { GridLayoutMine, PublicGridLayout } from './grid-layout-menu.interface';
import { GridLayoutMenuService } from './grid-layout-menu.service';
import { GridLayoutStore } from './grid-layout-store';

const PREVIEW_COLUMN_WIDTH = 80;

@Component({
  selector: 'app-grid-layout-manage',
  templateUrl: './grid-layout-manage.component.html',
  styleUrls: ['./grid-layout-manage.component.scss']
})
export class GridLayoutManageComponent implements OnInit, OnDestroy {

  @ViewChild('manageModal') manageModal: T2CardComponent;

  modalOpen = false;
  tab = 'mine';
  mine: GridLayoutMine = { haslayout: false, ispublic: false };
  publicLayouts: PublicGridLayout[] = [];
  search = '';
  publicName = '';
  publishEnabled = false;
  saving = false;
  preview: PublicGridLayout | null = null;
  previewColumnDefs: ColDef[] = [];
  previewColumnState: ColumnState[] = [];
  previewGrid: GridOptions = {
    rowData: [],
    overlayNoRowsTemplate: ' ',
    suppressMovableColumns: true,
    headerHeight: 24,
    groupHeaderHeight: 24,
    defaultColDef: { suppressMenu: true, suppressSorting: true, suppressMovable: true, suppressResize: true },
    onGridReady: () => this.applyPreviewState(),
    onNewColumnsLoaded: () => this.applyPreviewState()
  };
  publicGrid: GridOptions;
  private subscriptions: Subscription[] = [];

  constructor(
    private service: GridLayoutMenuService,
    private toastr: ToastrAlertHelper,
    private sweetAlert: SweetAlertHelper,
    private store: GridLayoutStore,
    private loading: T2LoadingService
  ) {
    this.publicGrid = this.buildPublicGrid();
  }

  get manageTitle(): string {
    const context = this.store.context;
    return 'Share Table Layout - ' + (context ? context.title : '');
  }

  get columnSummary(): string {
    const context = this.store.context;
    const columnApi = context && context.grid ? context.grid.columnApi : null;
    const state: ColumnState[] = columnApi ? columnApi.getColumnState() : [];
    const visible = state.filter(column => !column.hide).length;
    return 'This layout currently shows ' + visible + ' of ' + state.length + ' columns.';
  }

  get isMineTab(): boolean {
    return this.tab === 'mine';
  }

  ngOnInit() {
    this.listenRequests();
  }

  closeModal() {
    this.manageModal.close();
  }

  onManageAction(event: { isOpen: boolean }) {
    this.modalOpen = !!(event && event.isOpen);
  }

  showMine() {
    this.tab = 'mine';
  }

  showPublic() {
    this.tab = 'public';
    this.search = '';
    this.loadPublic();
  }

  onSearchChange(value: string) {
    this.search = value;
    this.clearPreview();
    if (this.publicGrid.api) {
      this.publicGrid.api.setQuickFilter(value);
    }
  }

  saveSharing() {
    if (this.saving) {
      return;
    }
    if (!this.publishEnabled) {
      this.stopSharing();
      return;
    }
    const config = this.store.readConfig();
    this.publicName = this.toTitleCase((this.publicName || '').trim());
    if (!this.publicName) {
      this.sweetAlert.captureException({ message: 'Public Name is required.' });
      return;
    }
    const moduleActionId = this.moduleActionId();
    if (!config || !moduleActionId) {
      this.sweetAlert.captureException({ message: 'This screen has no layout action.' });
      return;
    }
    this.startSaving();
    this.track(this.service.save(moduleActionId, config)
      .switchMap(() => this.service.getMine(moduleActionId))
      .switchMap((mine: GridLayoutMine) => {
        const layoutId = mine && mine.layoutid;
        if (!layoutId) {
          return Observable.throw({ message: 'Save your layout before publishing it.' });
        }
        return this.service.publish({ layoutid: layoutId, publicname: this.publicName });
      })
      .finally(() => this.stopSaving())
      .subscribe({
        next: () => {
          this.toastr.success('Layout sharing updated', 'Success');
          this.closeModal();
          this.store.notifyChanged();
        },
        error: error => this.sweetAlert.captureException(error)
      }));
  }

  selectPreview(item: PublicGridLayout) {
    if (!item || !item.layoutid) {
      return;
    }
    this.preview = item;
    this.track(this.service.getDetail(item.layoutid).subscribe({
      next: detail => {
        this.previewColumnState = this.columnsFromConfig(detail.config);
        this.previewColumnDefs = this.previewDefs(this.screenColumnDefs());
        this.applyPreviewState();
      },
      error: error => this.sweetAlert.captureException(error)
    }));
  }

  private listenRequests() {
    this.track(this.store.manage$.subscribe({ next: () => this.openManage() }));
    this.track(this.store.applyRequest$.subscribe({ next: item => this.confirmApply(item) }));
    this.track(this.store.unpublishRequest$.subscribe({ next: item => this.confirmUnpublish(item) }));
  }

  private openManage() {
    this.tab = 'mine';
    this.clearPreview();
    this.publicName = '';
    this.publishEnabled = false;
    this.search = '';
    this.loadMine();
    this.loadPublic();
    this.manageModal.open();
  }

  private loadMine() {
    const moduleActionId = this.moduleActionId();
    if (!moduleActionId) {
      return;
    }
    this.track(this.service.getMine(moduleActionId).subscribe({
      next: data => {
        this.mine = data || { haslayout: false, ispublic: false };
        this.publicName = this.mine.publicname || this.publicName;
        this.publishEnabled = !!this.mine.ispublic;
      },
      error: error => this.sweetAlert.captureException(error)
    }));
  }

  private loadPublic() {
    const moduleActionId = this.moduleActionId();
    if (!moduleActionId) {
      return;
    }
    this.track(this.service.getPublic(moduleActionId).subscribe({
      next: data => {
        this.publicLayouts = data || [];
      },
      error: error => this.sweetAlert.captureException(error)
    }));
  }

  private confirmApply(item: PublicGridLayout) {
    if (!item || !item.layoutid || item.ismine) {
      return;
    }
    const text = 'Apply ' + (item.publicname || 'this layout') + '? This replaces your saved layout for this grid.';
    this.confirm('Apply Layout', text, 'Apply', () => this.apply(item.layoutid));
  }

  private apply(layoutId: number) {
    this.track(this.service.apply(layoutId).subscribe({
      next: result => {
        if (!result || !this.store.applyConfig(result.config)) {
          this.sweetAlert.captureException({ message: 'The layout is not a valid column state.' });
          return;
        }
        this.toastr.success('Layout applied', 'Success');
        this.store.notifyChanged();
      },
      error: error => this.sweetAlert.captureException(error)
    }));
  }

  private confirmUnpublish(item: PublicGridLayout) {
    if (!item || !item.layoutid || !item.ismine) {
      return;
    }
    const text = 'Unpublish ' + (item.publicname || 'this layout') + '? Other users will no longer see it. Your layout stays saved.';
    this.confirm('Unpublish Layout', text, 'Unpublish', () => this.unpublish(item.layoutid));
  }

  private confirm(title: string, text: string, confirmButtonText: string, onConfirm: () => void) {
    this.sweetAlert.createCustomAlert({ title, text, type: 'warning', showCancelButton: true, confirmButtonText })
      .then(result => {
        if (result && result.value) {
          onConfirm();
        }
      });
  }

  private unpublish(layoutId: number, closeAfter = false) {
    this.startSaving();
    this.track(this.service.unpublish(layoutId).finally(() => this.stopSaving()).subscribe({
      next: () => {
        this.toastr.success('Layout is now private', 'Success');
        if (closeAfter) {
          this.closeModal();
        } else if (this.modalOpen) {
          this.loadPublic();
        }
        if (this.preview && this.preview.layoutid === layoutId) {
          this.clearPreview();
        }
        this.store.notifyChanged();
      },
      error: error => this.sweetAlert.captureException(error)
    }));
  }

  private startSaving() {
    this.saving = true;
    this.loading.show();
  }

  private stopSaving() {
    this.saving = false;
    this.loading.hiden();
  }

  private stopSharing() {
    const layoutId = this.mine && this.mine.ispublic ? this.mine.layoutid : null;
    if (!layoutId) {
      this.closeModal();
      return;
    }
    this.unpublish(layoutId, true);
  }

  private buildPublicGrid(): GridOptions {
    return <GridOptions> {
      enableSorting: true,
      enableFilter: false,
      enableColResize: true,
      rowSelection: 'single',
      suppressHorizontalScroll: true,
      defaultColDef: { suppressMenu: true },
      columnDefs: [
        { headerName: 'Name', field: 'publicname' },
        { headerName: 'Columns', field: 'visiblecolumns', width: 90, maxWidth: 110, suppressSizeToFit: true },
        {
          headerName: 'Updated',
          field: 'modifiedonformatted',
          width: 170,
          maxWidth: 180,
          suppressSizeToFit: true,
          comparator: (left, right, nodeA, nodeB) => {
            const leftTime = nodeA && nodeA.data ? new Date(nodeA.data.modifiedon).getTime() : 0;
            const rightTime = nodeB && nodeB.data ? new Date(nodeB.data.modifiedon).getTime() : 0;
            return leftTime - rightTime;
          }
        },
        {
          headerName: 'Actions',
          colId: 'actions',
          width: 80,
          suppressSizeToFit: true,
          suppressSorting: true,
          cellRendererFramework: BtnCellRendererComponent,
          cellRendererParams: (params: ICellRendererParams): BtnCellRendererI => this.actionButton(params.data)
        }
      ],
      onGridReady: event => {
        if (event && event.api) {
          event.api.sizeColumnsToFit();
        }
      },
      onCellClicked: event => {
        if (event && event.data && event.colDef.colId !== 'actions') {
          this.selectPreview(event.data);
        }
      }
    };
  }

  private actionButton(item: PublicGridLayout): BtnCellRendererI {
    const own = !!(item && item.ismine);
    return {
      classFontAwesome: own ? 'fa-eye-slash' : 'fa-check',
      bgColor: '#fff',
      color: own ? '#ee0000' : '#1464f6',
      border: '1px solid #ddd',
      title: own ? 'Unpublish layout' : 'Apply layout',
      onClick: () => own ? this.confirmUnpublish(item) : this.confirmApply(item)
    };
  }

  private applyPreviewState() {
    if (this.previewGrid.columnApi && this.previewColumnState.length) {
      this.previewGrid.columnApi.setColumnState(this.previewColumnState);
    }
  }

  private columnsFromConfig(config: string): ColumnState[] {
    try {
      const parsed = JSON.parse(config);
      const columns = parsed && !Array.isArray(parsed) ? parsed.columns : parsed;
      return Array.isArray(columns)
        ? columns.map((column: ColumnState) => this.withoutWidth(column))
        : [];
    } catch (e) {
      return [];
    }
  }

  private screenColumnDefs(): ColDef[] {
    const context = this.store.context;
    return (context && context.grid && context.grid.columnDefs ? context.grid.columnDefs : []) as ColDef[];
  }

  private previewDefs(defs: ColGroupDef[] | ColDef[]): ColDef[] {
    return (defs as Array<ColDef & ColGroupDef>).map(def => def.children
      ? <ColDef> { headerName: def.headerName, children: this.previewDefs(def.children) }
      : { colId: def.colId, headerName: def.headerName, field: def.field, width: this.compactWidth(def.width) });
  }

  private compactWidth(width: number | undefined): number {
    return Math.min(width || PREVIEW_COLUMN_WIDTH, PREVIEW_COLUMN_WIDTH);
  }

  private withoutWidth(column: ColumnState): ColumnState {
    const copy = <ColumnState> Object.assign({}, column);
    delete copy.width;
    return copy;
  }

  private toTitleCase(value: string): string {
    return value.toLowerCase().replace(/\S+/g, word => word.charAt(0).toUpperCase() + word.slice(1));
  }

  private clearPreview() {
    this.preview = null;
    this.previewColumnDefs = [];
    this.previewColumnState = [];
  }

  private moduleActionId(): number | null {
    const context = this.store.context;
    return context ? context.moduleActionId : null;
  }

  private track(subscription: Subscription) {
    this.subscriptions = this.subscriptions.filter(item => !item.closed);
    this.subscriptions.push(subscription);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/grid-layout-menu/grid-layout-manage.component.ts